import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import Checkbox from './Checkbox';
import { ChevronRight, ChevronLeft, CheckCircle } from 'lucide-react';

const ProfilePage = () => {
    const [user, setUser] = useState(null);
    const [teamData, setTeamData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState(null); // Local state for editing
    const navigate = useNavigate();

    const initialTeamMember = { salutation: '', name: '', bloodGroup: '', birthdate: '', contactNumber: '' }; // Define initial team member

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (authUser) => {
            if (authUser) {
                setUser(authUser);

                // Fetch team data based on the logged-in user's email
                try {
                    const teamsRef = collection(db, 'teams');
                    const q = query(teamsRef, where('email', '==', authUser.email));  // Assumes 'email' field in 'teams' collection stores team leader's email
                    const querySnapshot = await getDocs(q);

                    if (!querySnapshot.empty) {
                        // Assuming only one team per user; get the first document
                        const doc = querySnapshot.docs[0];
                        const data = doc.data();
                        setTeamData({ id: doc.id, ...data });
                        setFormData({ id: doc.id, ...data }); // Initialize form data
                    } else {
                        setTeamData(null); // No team found for this user
                        setFormData(null);
                    }
                } catch (error) {
                    console.error("Error fetching team data: ", error);
                    setTeamData(null); // Handle error (e.g., display an error message)
                    setFormData(null);
                } finally {
                    setLoading(false);
                }
            } else {
                setUser(null);
                setTeamData(null);
                setFormData(null);
                setLoading(false);
                navigate('/login'); // Redirect to login if not authenticated
            }
        });

        // Clean up the subscription
        return () => unsubscribe();
    }, [navigate]);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleCancelClick = () => {
        setIsEditing(false);
        setFormData(teamData); // Revert to original data
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAddTeamMember = () => {
        if (formData && formData.teamMembers && formData.teamMembers.length < 3) {
            setFormData(prev => ({
                ...prev,
                teamMembers: [...prev.teamMembers, { ...initialTeamMember }]
            }));
        } else {
            alert("Maximum 3 team members allowed.");
        }
    };

    const handleRemoveTeamMember = (index) => {
        const newTeamMembers = [...formData.teamMembers];
        newTeamMembers.splice(index, 1); // Remove the team member at the specified index
        setFormData(prev => ({
            ...prev,
            teamMembers: newTeamMembers
        }));
    };
    const handleTeamMemberChange = (index, field, value) => {
        const newTeamMembers = [...formData.teamMembers];
        newTeamMembers[index] = { ...newTeamMembers[index], [field]: value };
        setFormData(prev => ({
            ...prev,
            teamMembers: newTeamMembers
        }));
    };

    const handleSubmit = async () => {
        try {
            const teamDocRef = doc(db, 'teams', formData.id);
            const { email, ...dataToUpdate } = formData; // Exclude email from the update
            await updateDoc(teamDocRef, dataToUpdate);

            // Update the teamData state with the updated form data, keeping the original email
            setTeamData(prevTeamData => ({ ...dataToUpdate, email: prevTeamData.email }));

            setIsEditing(false); // Exit edit mode
            alert("Team details updated successfully!");

        } catch (error) {
            console.error("Error updating document: ", error);
            alert("There was an error updating your team details. Please try again.");
        }
    };


    if (loading) {
        return <div>Loading profile...</div>; // Or a more elaborate loading indicator
    }

    if (!user) {
        return <div>Please log in to view your profile.</div>; // Should ideally never be seen, as useEffect redirects
    }

    const capitalizeWords = (str) => {
        if (typeof str !== 'string') {
            return str; // Or handle non-string values in another way, like converting to a string
        }
        if (!str) return '';
        return str.replace(/\b\w/g, char => char.toUpperCase());
    };

    return (
        <div className="container mx-auto p-4">
            <div className="max-w-[1400px] mx-auto">
                <h1 className="text-3xl md:text-5xl font-bold mb-8 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    Team Information
                </h1>
            </div>

            {teamData && formData ? (
                <div>
                    {isEditing ? (
                        // EDIT MODE
                        <div className="space-y-6">
                            <h2 className="text-xl font-semibold mb-2">Edit Team Information</h2>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Team Name*</label>
                                <input type="text" name="teamName" value={formData.teamName || ''} onChange={handleInputChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Team Leader Salutation*</label>
                                <select name="teamLeaderSalutation" value={formData.teamLeaderSalutation || ''} onChange={handleInputChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                    <option value="">Select Salutation</option>
                                    <option value="Mr.">Mr.</option>
                                    <option value="Ms.">Ms.</option>
                                    <option value="Mrs.">Mrs.</option>
                                    <option value="Dr.">Dr.</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Team Leader Name*</label>
                                <input type="text" name="teamLeaderName" value={formData.teamLeaderName || ''} onChange={handleInputChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Team Leader's Blood Group*</label>
                                <select name="teamLeaderBloodGroup" value={formData.teamLeaderBloodGroup || ''} onChange={handleInputChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                    <option value="">Select Blood Group</option>
                                    <option value="A+">A+</option>
                                    <option value="A-">A-</option>
                                    <option value="B+">B+</option>
                                    <option value="B-">B-</option>
                                    <option value="O+">O+</option>
                                    <option value="O-">O-</option>
                                    <option value="AB+">AB+</option>
                                    <option value="AB-">AB-</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Team Leader's Birthdate*</label>
                                <input type="date" name="teamLeaderBirthdate" value={formData.teamLeaderBirthdate || ''} onChange={handleInputChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Email*</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email || ''}
                                    readOnly // Make the email field read-only
                                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-100"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Contact Number*</label>
                                <input type="tel" name="contactNumber" value={formData.contactNumber || ''} onChange={handleInputChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                            </div>

                            {/* Team Members Details */}
                            <div className="space-y-4">
                                <h3 className="text-lg font-semibold mt-4">Team Members:</h3>
                                {formData.teamMembers && Array.isArray(formData.teamMembers) && formData.teamMembers.map((member, index) => (
                                    <div key={index} className="space-y-1 border p-4 rounded-md">
                                        <h4 className="text-md font-semibold text-gray-700">Team Member {index + 1}</h4>
                                        {/* Remove button for team members */}
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTeamMember(index)}
                                            className="px-2 py-1 bg-red-500 text-white rounded-md hover:bg-red-700 text-sm"
                                        >
                                            Remove
                                        </button>

                                        <label className="block text-sm font-medium text-gray-700 mb-2">Salutation*</label>
                                        <select
                                            name={`teamMemberSalutation${index}`}
                                            value={member?.salutation || ''}
                                            onChange={(e) => handleTeamMemberChange(index, 'salutation', e.target.value)}
                                            className={`w-full px-4 py-3 rounded-lg border  focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                        >
                                            <option value="">Select Salutation</option>
                                            <option value="Mr.">Mr.</option>
                                            <option value="Ms.">Ms.</option>
                                            <option value="Mrs.">Mrs.</option>
                                            <option value="Dr.">Dr.</option>
                                        </select>

                                        <label className="block text-sm font-medium text-gray-700 mb-2">Name*</label>
                                        <input
                                            type="text"
                                            value={member?.name || ''}
                                            onChange={(e) => handleTeamMemberChange(index, 'name', e.target.value)}
                                            placeholder={`Enter Team Member ${index + 2} Name`}
                                            className={`w-full px-4 py-3 rounded-lg border  focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                        />

                                        <label className="block text-sm font-medium text-gray-700 mb-2">Blood Group*</label>
                                        <select
                                            name={`teamMemberBloodGroup${index}`}
                                            value={member?.bloodGroup || ''}
                                            onChange={(e) => handleTeamMemberChange(index, 'bloodGroup', e.target.value)}
                                            className={`w-full px-4 py-3 rounded-lg border  focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                        >
                                            <option value="">Select Blood Group</option>
                                            <option value="A+">A+</option>
                                            <option value="A-">A-</option>
                                            <option value="B+">B+</option>
                                            <option value="B-">B-</option>
                                            <option value="O+">O+</option>
                                            <option value="O-">O-</option>
                                            <option value="AB+">AB+</option>
                                            <option value="AB-">AB-</option>
                                        </select>

                                        <label className="block text-sm font-medium text-gray-700 mb-2">Birthdate*</label>
                                        <input
                                            type="date"
                                            value={member?.birthdate || ''}
                                            onChange={(e) => handleTeamMemberChange(index, 'birthdate', e.target.value)}
                                            className={`w-full px-4 py-3 rounded-lg border  focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                        />
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Contact Number*
                                        </label>
                                        <input
                                            type="tel"
                                            name={`teamMemberContactNumber${index}`}
                                            value={member?.contactNumber || ''}
                                            onChange={(e) => handleTeamMemberChange(index, 'contactNumber', e.target.value)}
                                            placeholder="Enter 10-digit mobile number"
                                            className={`w-full px-4 py-3 rounded-lg border  focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                                        />
                                    </div>
                                ))}
                                {/* Add team member button */}
                                {formData.teamMembers && Array.isArray(formData.teamMembers) && formData.teamMembers.length < 3 && (
                                    <button
                                        type="button"
                                        onClick={handleAddTeamMember}
                                        className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-700"
                                    >
                                        Add Team Member
                                    </button>
                                )}

                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Theme*</label>
                                <select name="theme" value={formData.theme || ''} onChange={handleInputChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                    <option value="">Select Theme</option>
                                    <option value="Social Impact">Social Impact</option>
                                    <option value="Economic Impact">Economic Impact</option>
                                    <option value="Visual Physics Software">Visual Physics Software</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Type*</label>
                                <select name="type" value={formData.type || ''} onChange={handleInputChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                                    <option value="">Select Type</option>
                                    <option value="Software">Software</option>
                                    <option value="Hardware">Hardware</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Institute Name*</label>
                                <input type="text" name="instituteName" value={formData.instituteName || ''} onChange={handleInputChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Project Title*</label>
                                <input type="text" name="projectTitle" value={formData.projectTitle || ''} onChange={handleInputChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Project Description*</label>
                                <textarea name="projectDescription" value={formData.projectDescription || ''} onChange={handleInputChange} rows={4} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500" />
                            </div>

                            <div className="flex justify-between">
                                <button onClick={handleSubmit} className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-700">Save</button>
                                <button onClick={handleCancelClick} className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400">Cancel</button>
                            </div>
                        </div>
                    ) : (
                        // VIEW MODE
                        <div className="space-y-6">
                            {/* Data display here */}
                            <div className="space-y-6">
                                <p><strong>Team Name:</strong> {teamData.teamName}</p>
                                <p><strong>Team Leader:</strong> {teamData.teamLeaderSalutation} {teamData.teamLeaderName}</p>
                                <p><strong>Email:</strong> {teamData.email}</p>
                                <p><strong>Contact Number:</strong> {teamData.contactNumber}</p>
                                <p><strong>Theme:</strong> {teamData.theme}</p>
                                <p><strong>Type:</strong> {teamData.type}</p>
                                <p><strong>Project Title:</strong> {teamData.projectTitle}</p>
                                <p><strong>Project Description:</strong> {teamData.projectDescription}</p>
                                <p><strong>Institute Name:</strong> {teamData.instituteName}</p>

                                {teamData.teamMembers && teamData.teamMembers.length > 0 && (
                                    <div>
                                        <h3 className="text-lg font-semibold mt-4">Team Members:</h3>
                                        <ul>
                                            {teamData.teamMembers.map((member, index) => (
                                                <li key={index}>
                                                    {member.salutation} {member.name} (Blood Group: {member.bloodGroup}, Birthdate: {member.birthdate}, Contact Number: {member.contactNumber})
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <button
                                    onClick={handleEditClick}
                                    className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-700"
                                >
                                    Edit Team Details
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <div className="text-center">
                    <p className="mb-4">No team data found for this user. Register a team to participate!</p>
                    <Link
                        to={`/register?step=1`}  // Pass step=1 in the URL
                        className="px-4 py-2 bg-green-500 text-white rounded-md hover:bg-green-700 inline-block"
                    >
                        Add Team
                    </Link>
                </div>
            )}
        </div>
    );
};

export default ProfilePage;